import { useEffect, useState } from "react";
import axios from "axios";
import { X, Mail, Phone, Loader2, Download, CheckCircle2 } from "lucide-react";
import { useLang } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * LeadMagnetModal intercepts a catalogue download to collect email + WhatsApp.
 * Triggers an actual file download (props.href) when submitted or skipped.
 */
export default function LeadMagnetModal({ open, onClose, fileHref, fileName = "ikel-mt-catalogue.pdf" }) {
  const { t, lang } = useLang();
  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  const triggerDownload = () => {
    if (!fileHref) return;
    const a = document.createElement("a");
    a.href = fileHref;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !whatsapp.trim()) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/leads`, {
        email: email.trim(),
        whatsapp: whatsapp.trim(),
        source: "catalogue_download",
        language: lang,
      });
      setStatus("success");
      triggerDownload();
      setTimeout(() => {
        setStatus("idle");
        setEmail("");
        setWhatsapp("");
        onClose();
      }, 1600);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(Array.isArray(detail) ? detail.map((d) => d.msg).join(", ") : detail || t.common.errorMsg);
      setStatus("idle");
    }
  };

  const skip = () => {
    triggerDownload();
    onClose();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[110] bg-[#0B1B29]/80 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="lead-magnet-modal"
      role="dialog"
      aria-modal="true"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white w-full max-w-md relative shadow-2xl">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center hover:bg-[#F5F5F7] transition-colors"
          aria-label={t.common.catalogue.close}
          data-testid="lead-magnet-close"
        >
          <X size={18} />
        </button>

        <div className="bg-[#0B1B29] text-white px-7 pt-10 pb-6">
          <div className="inline-flex items-center gap-2 bg-[#F59E0B] text-[#0B1B29] text-[10px] font-bold tracking-[0.22em] uppercase px-2.5 py-1 mb-5">
            <Download size={12} /> İKEL MT · 2026
          </div>
          <h3 className="text-2xl md:text-3xl font-black tracking-tighter leading-tight">
            {t.common.leadMagnet.heading}
          </h3>
          <p className="mt-3 text-white/75 text-sm leading-relaxed">
            {t.common.leadMagnet.subtitle}
          </p>
        </div>

        {status === "success" ? (
          <div className="px-7 py-10 flex flex-col items-start gap-3" data-testid="lead-magnet-success">
            <CheckCircle2 size={36} className="text-[#22C55E]" />
            <p className="text-[#0B1B29] font-semibold">{t.common.leadMagnet.success}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="px-7 py-6 space-y-4" data-testid="lead-magnet-form">
            <div>
              <label className="pgb-label" htmlFor="lm-email">
                {t.common.leadMagnet.emailLabel} *
              </label>
              <div className="relative">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#536878] pointer-events-none" />
                <input
                  id="lm-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pgb-input pl-9"
                  data-testid="lead-magnet-email"
                  placeholder="name@company.com"
                  disabled={status === "loading"}
                />
              </div>
            </div>

            <div>
              <label className="pgb-label" htmlFor="lm-whatsapp">
                {t.common.leadMagnet.whatsappLabel} *
              </label>
              <div className="relative">
                <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#536878] pointer-events-none" />
                <input
                  id="lm-whatsapp"
                  type="tel"
                  required
                  value={whatsapp}
                  onChange={(e) => setWhatsapp(e.target.value)}
                  className="pgb-input pl-9"
                  data-testid="lead-magnet-whatsapp"
                  placeholder={t.common.leadMagnet.whatsappPlaceholder}
                  disabled={status === "loading"}
                />
              </div>
            </div>

            {error && (
              <div className="text-red-600 text-xs" data-testid="lead-magnet-error">{error}</div>
            )}

            <button
              type="submit"
              disabled={status === "loading" || !email || !whatsapp}
              className="btn-accent w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="lead-magnet-submit"
            >
              {status === "loading" ? (
                <><Loader2 size={16} className="animate-spin" /> {t.common.leadMagnet.sending}</>
              ) : (
                <><Download size={16} /> {t.common.leadMagnet.submit}</>
              )}
            </button>

            <button
              type="button"
              onClick={skip}
              className="w-full text-xs text-[#536878] hover:text-[#0B1B29] tracking-wide underline underline-offset-2"
              data-testid="lead-magnet-skip"
            >
              {t.common.leadMagnet.skip}
            </button>

            <p className="text-[11px] text-[#536878] leading-relaxed pt-1">
              {t.common.leadMagnet.privacy}
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
